import { observer, useLocalObservable } from 'mobx-react';
import IconButton, {
  ICON_BUTTON_TYPES,
  ICON_BUTTON_ICONS
} from '../../../components/buttons/IconButton';
import { useStore } from '../../../store/store';
import { formatNumber, getTooltipId } from '../../../utils/utils';
import ChipsList from '../../../components/common/ChipsList';
import RegisterStatusBadge from '../../../components/common/RegisterStatusBadge';
import { PUNCTUATION } from '../../../utils/constants/notation';
import { Tooltip } from 'react-tooltip';
import { eventWatchlistCheckUpdatesClicked } from '../../../ga4/ga4';
import { openInNewTabOnModifier } from '../../../utils/openInNewTab';

const WatchlistCompanyResult = observer(
  ({ company, displayDetails = () => {}, setIsAddRemoveWatchlistPopupOpen = () => {} }) => {
    const { utilsStore, profilerProjectStore } = useStore();
    const state = useLocalObservable(() => ({
      nameTooltipId: getTooltipId()
    }));

    const openDetailsInNewTab = () =>
      utilsStore.openCompanyDetails(company.id, { ref: 'watchlist' });

    // Cmd/Ctrl-click opens a new tab; a plain click opens details in place. The
    // right-click "Open in new tab" menu shares the same openDetailsInNewTab thunk.
    const onOpenDetails = openInNewTabOnModifier(openDetailsInNewTab, () =>
      displayDetails(company)
    );

    return (
      <div key={company.id} className="item">
        <div className="cols">
          <div className="col company-name">
            <div className="label">Company name</div>
            <div className="value">
              <span
                id={state.nameTooltipId}
                className="company-name-link"
                onClick={onOpenDetails}
              >
                {company.name || PUNCTUATION.EMPTY_VALUE}
              </span>
              <Tooltip
                anchorSelect={`#${state.nameTooltipId}`}
                multiline={true}
                variant="dark"
                place={'bottom-center'}
                opacity={1}
                content={'View details'}
                className={'company-name-view-details-tooltip'}
              />
              <RegisterStatusBadge company={company} />
            </div>
          </div>
          <div className="col">
            <div className="label">Location</div>
            <div className="value">{company.headquarters || PUNCTUATION.EMPTY_VALUE}</div>
          </div>
          <div className="col">
            <div className="label">Industry</div>
            <div className="value">
              {/* {company.industries && company.industries.length
                ? utilsStore.shortenList(company.industries, 3).join(', ')
                : PUNCTUATION.EMPTY_VALUE} */}
              <ChipsList
                list={company.industries}
                alignTopMeasurementsEnabled
                alignTopMeasurementsRowQuerySelector=".page-profiler-watch-list > .listing-layout > .results > .item > .cols"
                alignTopMeasurementsItemsContainersQuerySelector="> .col"
              />
            </div>
          </div>
          <div className="col">
            <div className="label">Value chain</div>
            <div className="value">
              {/* {company.valueChain && company.valueChain.length
                ? utilsStore.shortenList(company.valueChain, 3).join(', ')
                : PUNCTUATION.EMPTY_VALUE} */}
              <ChipsList list={company.valueChain} />
            </div>
          </div>
          <div className="col">
            <div className="label">Products</div>
            <div className="value">
              {/* {company.products && company.products.length
                ? utilsStore.shortenList(company.products, 3).join(', ')
                : PUNCTUATION.EMPTY_VALUE} */}
              <ChipsList list={company.products} />
            </div>
          </div>
          <div className="col company-revenue">
            <div className="label">Sales (€M)</div>
            <div className="value right-align-desktop">
              {company.revenue ? (
                <>
                  {company.revenue}
                  <span className="year">
                    &lsquo;
                    {company.revenueYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
                  </span>
                </>
              ) : (
                PUNCTUATION.EMPTY_VALUE
              )}
            </div>
          </div>
          <div className="col no-border company-employees">
            <div className="label">Employees</div>
            <div className="value right-align-desktop">
              {!isNaN(company.employees) ? (
                <>
                  {formatNumber((+company.employees).toFixed(2))}
                  <span className="year">
                    &lsquo;
                    {company.employeesYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
                  </span>
                </>
              ) : (
                PUNCTUATION.EMPTY_VALUE
              )}
            </div>
          </div>
        </div>
        <div className="actions watchlist">
          {' '}
          <div className="col no-border view-details-btn">
            <IconButton
              filled
              type={ICON_BUTTON_TYPES.BLUE}
              onClick={openInNewTabOnModifier(openDetailsInNewTab, () => {
                eventWatchlistCheckUpdatesClicked(company.id);
                displayDetails(company);
              })}
              innerText="Check updates"
              disabled={!!profilerProjectStore.companiesInWatchlistAction[company.id]}
              wrapClassName="check-updates-watchlist-button"
              linkContextMenu={[
                {
                  icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                  text: 'Open in new tab',
                  onClick: () => {
                    utilsStore.openCompanyDetails(company.id, { ref: 'watchlist' });
                  }
                }
              ]}
            />
          </div>
          <div className="col no-border">
            <IconButton
              type={ICON_BUTTON_TYPES.RED}
              icon={ICON_BUTTON_ICONS.X_LG}
              tooltipText="Remove from watchlist"
              onClick={() => setIsAddRemoveWatchlistPopupOpen(company)}
              disabled={!!profilerProjectStore.companiesInWatchlistAction[company.id]}
              wrapClassName="remove-from-watchlist-button"
              tooltipPosition={utilsStore.windowWidth > 1200 ? 'bottom-center' : 'bottom-end'}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default WatchlistCompanyResult;
